/**
 * 要件定義テーブル（戦闘力サマリーの PDG 列の下）。
 *
 * PDG は ACE より人数が少なく、全体ビューの戦闘力サマリーは左（PDG）の下に空きが出る。
 * そこへ **いま要件定義セット子票を持っているエンジニアだけ** を抜いた表を置き、
 * 「その人の当月実績のどれだけが要件定義か」を一覧で読めるようにする。
 *
 * 口径も字面も上の戦闘力テーブルの実績Pt に揃える: **当月の完了 + 処理中（現在スナップショット）**を
 * `完了(処理中)` 形式で出し、見出しの括弧は 2 行目へ落とす。**合計行だけは表示中の行ではなく
 * メンバー全員が母数**で、総実績ptは上のテーブルの合計と一致する。
 * 見出しは要件定義系の色（水色 `REQUIREMENT_COLOR`）にして、同じカードに 2 つ並ぶ
 * 戦闘力テーブルと別物だと一目で分かるようにする（KPI 画面の要件定義内訳と同じ色）。
 *
 * 実績 3 列の右に **未対応pt（要件定義）**（assign 済みで status=未対応 の要件定義セット子票の
 * 予定pt）と、その pt が **今日起点 1 ヶ月の稼働をどれだけ埋めるか**（稼働占有率）を足す。
 * 実績 3 列が「当月に何をしたか」なのに対し、こちらは「これから何を抱えているか」で、
 * 占有率の算出は全体PJ看板のレビュアーリソースと同じロジック（当月/翌月 capacity を
 * 営業日按分 → 予定pt ÷ capacity）を API 側で共有する。未対応は現在スナップショットのため
 * **当月を表示しているときだけ**出す（過去月の実績の隣に「いまの未対応」を並べない）。
 *
 * 稼働占有率が API の返すしきい値を超えた行は赤字にする（レビュアーリソースカードと同じ色・
 * 同じ凡例の出し方）。しきい値の数字はフロントに置かず、サーバーの値をそのまま使う。
 *
 * 行はクリックでその人の要件定義pt 明細モーダル（処理済み = 完了 + 処理中 / 待処理 = 未対応）を
 * 開く。担当者名のリンク（メンバー詳細ページ）はクリックを伝播させず、従来どおり遷移する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 当月に要件定義セット子票の実績pt または未対応ptを持つエンジニアの要件定義pt / 開発pt / 総実績pt (いずれも完了+処理中) ・要件定義割合・未対応pt(要件定義)と稼働占有率を、水色見出しのテーブルで一覧する。行クリックで明細モーダルを開く。戦闘力サマリー全体ビューの PDG 列下に出す
 * @graph-connects buildRequirementDefinitionRows [calls] 要件定義pt / 未対応pt を持つメンバーの行組み立て
 * @graph-connects sumRequirementDefinitionTotals [calls] 合計行の算出 (母数はメンバー全員)
 * @graph-connects TwoLineHeaderLabel [calls] 「（処理中含め）」を 2 行目へ落とす見出し
 * @graph-connects REQUIREMENT_COLOR [references] 要件定義系の見出し色 (水色)
 * @graph-connects RequirementMemberDetailModal [calls] 行クリックで開く 1 名分の要件定義pt 明細モーダル
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { MonthlyCombatPower } from '../../../types/combat-power.js';
import { REQUIREMENT_COLOR } from '../kpi/develop-point-view.js';
import { useRequirementPendingResources } from '../../../hooks/useCombatPower.js';
import { jstCurrentYm } from '../../../lib/fiscal-quarter.js';
import { formatNum, formatPtWithWip, monthHeaderToYyyymm } from './combat-power-utils.js';
import { TwoLineHeaderLabel } from './TwoLineHeaderLabel.js';
import { RequirementMemberDetailModal } from './RequirementMemberDetailModal.js';
import {
  buildRequirementDefinitionRows,
  sumRequirementDefinitionTotals,
  type PtSplit,
  type RequirementDefinitionRow,
  type RequirementPendingByName,
} from './requirement-definition-rows.js';

interface RequirementDefinitionTableProps {
  /**
   * 対象メンバー（PDG 母集団）。**行になるのは要件定義ptを持つ人だけだが、合計行は
   * この全員が母数**（総実績ptを上の戦闘力テーブルの合計と一致させるため）。
   */
  members: MonthlyCombatPower[];
  /** 表示中の月インデックス */
  currentMonthIdx: number;
  /**
   * 担当者名 (小文字 nickName) → メールアドレスの対応表。
   * 引き当てられた担当者名はメンバー詳細ページ (`/members/$email`) へのリンクになる。
   */
  nameToEmail?: Map<string, string>;
  /**
   * 表示中の月ヘッダー ("2026-10月")。未対応列を出すか（= 当月表示か）の判定と、
   * 明細モーダルの完了明細の取得範囲に使う。
   */
  monthHeader: string;
}

/** 割合セルの表示 (`-` フォールバック込み)。 @graph-connects none */
function formatPercent(percent: number | null): string {
  return percent === null ? '-' : `${formatNum(percent, 1)}%`;
}

/** pt セル。上の戦闘力テーブルの実績Pt 列と同じ `完了(処理中)` 形式で出す。 @graph-connects none */
function PtCell({ pt, emphasized = false }: { pt: PtSplit; emphasized?: boolean }) {
  return (
    <td
      className={`py-1.5 px-2 text-right whitespace-nowrap ${
        emphasized
          ? 'font-medium text-[var(--color-text-primary)]'
          : 'text-[var(--color-text-secondary)]'
      }`}
    >
      {formatPtWithWip(pt.completed, pt.inProgress)}
    </td>
  );
}

/** 合計行の pt セル。 @graph-connects none */
function TotalPtCell({ pt, emphasized = false }: { pt: PtSplit; emphasized?: boolean }) {
  return (
    <td
      className={`py-2 px-2 text-right whitespace-nowrap ${
        emphasized ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'
      }`}
    >
      {formatPtWithWip(pt.completed, pt.inProgress)}
    </td>
  );
}

/**
 * 未対応pt セル。現在スナップショットの単一値なので `完了(処理中)` 形式にはしない。
 *
 * **件数は母票（案件）単位**。要件定義セットは 1 案件につき 5 工程の子票が自動生成されるため、
 * 子票を数えると案件数の数倍になり「何案件抱えているか」が読めない。hover では案件数と
 * 生の票数の両方を出す。
 *
 * **pt の多少に関わらず常に `pt(案件数)` で出す**（`9(1件)` / `0(4件)`）。未対応の要件定義票は
 * 予定pt 未入力のものが実データで多数あり（2026-10 時点で担当者の 6 割強が 0pt）、pt だけだと
 * `0` が「抱えていない」に見えるうえ、同じ 9pt でも 1 案件なのか 5 案件なのかで意味が違う。
 * `-` は **未対応票を 1 件も持たない** ときだけ。
 * @graph-connects none
 */
function PendingPtCell({ pending }: { pending: RequirementDefinitionRow['pending'] }) {
  const { t } = useTranslation();
  if (pending === null) {
    return <td className="py-1.5 px-2 text-right text-[var(--color-text-muted)]">-</td>;
  }
  return (
    <td
      className="py-1.5 px-2 text-right whitespace-nowrap font-medium text-[var(--color-text-primary)]"
      title={t('members.combatPower.requirementTable.pendingCountHint', {
        projects: pending.projectCount,
        tickets: pending.ticketCount,
      })}
    >
      {formatNum(pending.pendingPoint, 1)}
      <span className="ml-0.5 font-normal text-[var(--color-text-muted)]">
        {t('members.combatPower.requirementTable.pendingProjectCount', {
          value: pending.projectCount,
        })}
      </span>
    </td>
  );
}

/**
 * 占有率セル。capacity（今日起点 1 ヶ月）を持たないメンバーは `-`。
 * capacity は hover で添え、「何ptの枠に対しての割合か」を追えるようにする。
 *
 * **しきい値を超えた行は赤字**。しきい値の数字はフロントに置かず API が返す値をそのまま使い、
 * 色は全体PJ看板のレビュアーリソースカードと同じ `--color-error` に揃える。
 * @graph-connects none
 */
function OccupancyCell({
  pending,
  warnThresholdPercent,
}: {
  pending: RequirementDefinitionRow['pending'];
  /** この値を超えたら赤字にする。API 未取得のうちは undefined（色分けしない） */
  warnThresholdPercent: number | undefined;
}) {
  const { t } = useTranslation();
  if (pending === null || pending.occupancyPercent === null) {
    return <td className="py-1.5 px-2 text-right text-[var(--color-text-muted)]">-</td>;
  }
  const isOver =
    warnThresholdPercent !== undefined && pending.occupancyPercent > warnThresholdPercent;
  return (
    <td
      className={`py-1.5 px-2 text-right whitespace-nowrap font-medium ${
        isOver ? 'text-[var(--color-error)]' : 'text-[var(--color-text-primary)]'
      }`}
      title={t('members.combatPower.requirementTable.capacityHint', {
        pending: formatNum(pending.pendingPoint, 1),
        capacity: formatNum(pending.capacity, 1),
      })}
    >
      {formatNum(pending.occupancyPercent, 1)}%
    </td>
  );
}

/** @graph-connects none */
export function RequirementDefinitionTable({
  members,
  currentMonthIdx,
  nameToEmail,
  monthHeader,
}: RequirementDefinitionTableProps) {
  const { t } = useTranslation();
  const { data: pendingData } = useRequirementPendingResources();
  // しきい値はサーバーの値をそのまま使う（同じ数字をフロントにも置かない）
  const warnThresholdPercent = pendingData?.warnThresholdPercent;
  /** 明細モーダルの対象者（null = 閉じている） */
  const [detailTarget, setDetailTarget] = useState<{
    nickname: string;
    email: string;
    row: RequirementDefinitionRow;
  } | null>(null);

  // 未対応pt は現在スナップショットなので、当月を表示しているときだけ列に出す。
  // 過去月の実績の隣に「いまの未対応」を並べると、同じ行の数字が別の時点を指してしまう。
  const pendingByName: RequirementPendingByName | undefined = useMemo(() => {
    if (monthHeaderToYyyymm(monthHeader) !== jstCurrentYm() || !pendingData) return undefined;
    return new Map(pendingData.data.map((r) => [r.nickname.toLowerCase(), r]));
  }, [monthHeader, pendingData]);

  const rows = useMemo(
    () => buildRequirementDefinitionRows(members, currentMonthIdx, pendingByName),
    [members, currentMonthIdx, pendingByName],
  );
  // 合計は行ではなくメンバー全員から出す（総実績ptを上の戦闘力テーブルの合計と揃えるため）
  const totals = useMemo(
    () => sumRequirementDefinitionTotals(members, currentMonthIdx, pendingByName),
    [members, currentMonthIdx, pendingByName],
  );

  return (
    <div className="min-w-0">
      {/* 見出しは要件定義系の水色。左ボーダー + 薄い背景の帯は ACE サブチーム表と同じ作り */}
      <div
        className="mb-3 rounded-md px-3 py-1.5"
        style={{
          backgroundColor: `${REQUIREMENT_COLOR}1A`,
          borderLeft: `4px solid ${REQUIREMENT_COLOR}`,
        }}
      >
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-base font-bold" style={{ color: REQUIREMENT_COLOR }}>
            {t('members.combatPower.requirementTable.title')}
          </h3>
          {/* 赤字の判定基準（しきい値）の凡例。レビュアーリソースカードと同じ作り */}
          {warnThresholdPercent !== undefined && (
            <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]">
              <span className="inline-block size-1.5 rounded-full bg-[var(--color-error)]" />
              <span className="font-mono tabular-nums">
                {t('members.combatPower.requirementTable.warnLegend', {
                  value: warnThresholdPercent,
                })}
              </span>
            </span>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-xs text-[var(--color-text-muted)]">
          {t('members.combatPower.requirementTable.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  {t('members.combatPower.name')}
                </th>
                {/* 「（処理中含め）」は上の戦闘力テーブルと同じく 2 行目へ落とす（同じ字面・同じ寄せ） */}
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel
                    label={t('members.combatPower.requirementTable.requirementPt')}
                  />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel label={t('members.combatPower.requirementTable.developPt')} />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel label={t('members.combatPower.requirementTable.totalPt')} />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  {t('members.combatPower.requirementTable.requirementRatio')}
                </th>
                {/* ここから右 2 列は当月実績ではなく「これから」（現在スナップショット） */}
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel label={t('members.combatPower.requirementTable.pendingPt')} />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)]">
                  <TwoLineHeaderLabel
                    label={t('members.combatPower.requirementTable.pendingOccupancy')}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                // 明細の取得キーはメール。対応表に無ければ待処理 API が返した値で補う
                // （退職者等で unified_members 側の引き当てが外れても明細は開けるようにする）
                const memberEmail = nameToEmail?.get(row.name.toLowerCase()) ?? row.pending?.email;
                return (
                  <tr
                    key={row.name}
                    // 行ごと押せる（担当者名のリンクだけは stopPropagation で遷移を保つ）。
                    // メールが引けない行は明細を取得できないのでクリックを無効にする
                    className={`border-b border-[var(--color-border)] last:border-b-0 transition-colors ${
                      memberEmail === undefined
                        ? ''
                        : 'cursor-pointer hover:bg-[var(--color-bg-hover)]'
                    }`}
                    onClick={
                      memberEmail === undefined
                        ? undefined
                        : () => {
                            setDetailTarget({ nickname: row.name, email: memberEmail, row });
                          }
                    }
                  >
                    <td className="py-1.5 px-2 font-medium">
                      {memberEmail !== undefined ? (
                        <Link
                          to="/members/$email"
                          params={{ email: memberEmail }}
                          className="text-[var(--color-accent-primary)] hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {row.name}
                        </Link>
                      ) : (
                        <span className="text-[var(--color-text-primary)]">{row.name}</span>
                      )}
                    </td>
                    <PtCell pt={row.requirement} emphasized />
                    <PtCell pt={row.develop} />
                    <PtCell pt={row.total} />
                    <td className="py-1.5 px-2 text-right font-medium text-[var(--color-text-primary)]">
                      {formatPercent(row.requirementPercent)}
                    </td>
                    <PendingPtCell pending={row.pending} />
                    <OccupancyCell
                      pending={row.pending}
                      warnThresholdPercent={warnThresholdPercent}
                    />
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] font-bold">
                <td className="py-2 px-2 text-[var(--color-text-primary)]">
                  {t('members.combatPower.teamTotal')}
                </td>
                <TotalPtCell pt={totals.requirement} emphasized />
                <TotalPtCell pt={totals.develop} />
                <TotalPtCell pt={totals.total} />
                <td className="py-2 px-2 text-right text-[var(--color-text-primary)]">
                  {formatPercent(totals.requirementPercent)}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap text-[var(--color-text-primary)]">
                  {totals.pendingPoint === null ? (
                    '-'
                  ) : (
                    <>
                      {formatNum(totals.pendingPoint, 1)}
                      {/* 合計の案件数は行の単純和ではなく母票の重複排除（同じ案件を
                          複数人が持つと行を足し上げた数より少なくなる） */}
                      <span className="ml-0.5 font-normal text-[var(--color-text-muted)]">
                        {t('members.combatPower.requirementTable.pendingProjectCount', {
                          value: totals.pendingProjectCount ?? 0,
                        })}
                      </span>
                    </>
                  )}
                </td>
                {/* 占有率の合計は出さない（分母の capacity は待処理ptを持つメンバーの分しか
                    取得していないため、合計で割ると列見出しと違う意味の数になる） */}
                <td className="py-2 px-2 text-right text-[var(--color-text-muted)]">-</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {/* 口径の注記。上の戦闘力テーブルの実績Pt と同じ集め方だと明示しないと、
          同じカード内で合計が違って見えたときに読み手が理由を追えない */}
      <p className="mt-2 text-[10px] text-[var(--color-text-muted)]">
        {t('members.combatPower.requirementTable.note')}
      </p>
      {/* 未対応の口径（母票完了/保留中の除外・占有率の分母）は実績とは別なので節を分ける */}
      <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
        {t('members.combatPower.requirementTable.pendingNote')}
      </p>
      <RequirementMemberDetailModal
        open={detailTarget !== null}
        onClose={() => {
          setDetailTarget(null);
        }}
        nickname={detailTarget?.nickname ?? ''}
        email={detailTarget?.email ?? ''}
        monthHeader={monthHeader}
        pendingProjects={detailTarget?.row.pending?.projects ?? []}
      />
    </div>
  );
}
